import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft, faStar, faTrophy, faBed, faPhone, faEnvelope,
  faComment, faCakeCandles, faPen, faCalendarCheck, faMoneyBill,
  faPassport, faTag, faStickyNote, faPlus, faXmark, faFloppyDisk,
  faBrain, faLink, faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./GuestProfilePage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function avatarColor(name: string) {
  const colors = ["#1a3a5c","#1a3a5c","#ec4899","#f59e0b","#10b981","#1a3a5c","#ef4444","#14b8a6"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function getInitials(name: string) {
  return name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_COLORS: Record<string, string> = {
  confirmed:      "#10b981",
  "checked-in":   "#1a3a5c",
  "checked-out":  "#64748b",
  cancelled:      "#ef4444",
  pending:        "#f59e0b",
};

export default function GuestProfilePage() {
  const { phone } = useParams<{ phone: string }>();
  const navigate  = useNavigate();

  const [guest, setGuest]       = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [passport, setPassport] = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  // CRM
  const [crm, setCrm]           = useState<{ notes: string; tags: string[]; preferences: any }>({ notes: "", tags: [], preferences: {} });
  const [crmEditing, setCrmEditing] = useState(false);
  const [crmDraft, setCrmDraft] = useState({ notes: "", tags: [] as string[], preferences: {} as any });
  const [newTag, setNewTag]     = useState("");
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmToast, setCrmToast] = useState("");

  // Feedback
  const [feedbackList, setFeedbackList]     = useState<any[]>([]);
  const [fbLinkCopied, setFbLinkCopied]     = useState(false);
  const [fbGenerating, setFbGenerating]     = useState(false);

  const token      = localStorage.getItem("ai2_token") ?? "";
  const hotel      = localStorage.getItem("ai2_hotel") ?? "";
  const authHeaders = { Authorization: `Bearer ${token}`, "x-hotel": hotel };

  const showCrmToast = (msg: string) => { setCrmToast(msg); setTimeout(() => setCrmToast(""), 2500); };

  useEffect(() => {
    if (!phone) return;
    setLoading(true);

    Promise.all([
      axios.get(`${API_BASE}/api/dashboard/guests/${phone}/bookings`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/dashboard/guests/${phone}/crm`, { headers: authHeaders }).catch(() => ({ data: { notes: "", tags: [], preferences: {} } })),
    ]).then(async ([guestRes, crmRes]) => {
      const guestData   = guestRes.data.guest ?? null;
      const bookingData = guestRes.data.bookings ?? [];
      setGuest(guestData);
      setBookings(bookingData);

      const crmData = crmRes.data || { notes: "", tags: [], preferences: {} };
      setCrm(crmData);
      setCrmDraft({ notes: crmData.notes || "", tags: crmData.tags || [], preferences: crmData.preferences || {} });

      // Load passport for the most recent booking
      const sortedBookings = [...bookingData].sort((a: any, b: any) =>
        (b.CheckIn || "").localeCompare(a.CheckIn || "")
      );
      const latestBookingId = sortedBookings[0]?.BookingID;
      if (latestBookingId) {
        axios.get(`${API_BASE}/api/passports/${encodeURIComponent(latestBookingId)}`, { headers: authHeaders })
          .then(r => setPassport(r.data))
          .catch(() => setPassport(null));
      }

      // Load feedback
      axios.get(`${API_BASE}/api/feedback/guest/${encodeURIComponent(phone!)}`, { headers: authHeaders })
        .then(r => setFeedbackList(r.data || []))
        .catch(() => setFeedbackList([]));
    }).catch(err => {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load guest");
    }).finally(() => setLoading(false));
  }, [phone]);

  const sendFeedbackLink = async () => {
    const latestBooking = [...bookings].sort((a: any, b: any) =>
      (b.CheckIn || "").localeCompare(a.CheckIn || "")
    )[0];
    if (!latestBooking) return;
    setFbGenerating(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/feedback/generate`, {
        bookingId: latestBooking.BookingID,
        guestName: guest?.name,
      }, { headers: authHeaders });
      const link = `${window.location.origin}/feedback/${data.token}?h=${hotel}`;
      navigator.clipboard.writeText(link).catch(() => {});
      setFbLinkCopied(true);
      setTimeout(() => setFbLinkCopied(false), 2500);
      showCrmToast("Feedback link copied!");
    } catch {
      showCrmToast("Failed to generate link");
    } finally {
      setFbGenerating(false);
    }
  };

  const saveCrm = async () => {
    setCrmSaving(true);
    try {
      const { data } = await axios.put(`${API_BASE}/api/dashboard/guests/${phone}/crm`, crmDraft, { headers: authHeaders });
      setCrm(data);
      setCrmEditing(false);
      showCrmToast("CRM saved");
    } catch {
      showCrmToast("Failed to save");
    } finally {
      setCrmSaving(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="gpp-loading">Loading guest profile…</div>
    </DashboardLayout>
  );

  if (error || !guest) return (
    <DashboardLayout>
      <div className="gpp-error">
        <p>{error || "Guest not found."}</p>
        <button onClick={() => navigate(-1)} className="gpp-back-btn">← Back</button>
      </div>
    </DashboardLayout>
  );

  const name       = guest.name || "Unknown";
  const color      = avatarColor(name);
  const totalSpent = bookings.reduce((s: number, b: any) => s + (Number(b.Total) || 0), 0);
  const totalNights = bookings.reduce((s: number, b: any) => s + (Number(b.Nights) || 0), 0);
  const activeBooking = bookings.find((b: any) =>
    ["confirmed","checked-in"].includes((b.Status || "").toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="gpp-page">

        {/* Back */}
        <button className="gpp-back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Guests
        </button>

        <div className="gpp-layout">

          {/* ── LEFT: Profile Card ─────────────────────────────── */}
          <aside className="gpp-sidebar">
            <div className="gpp-card">
              {/* Avatar */}
              <div className="gpp-avatar" style={{ background: color }}>
                {getInitials(name)}
              </div>
              <h1 className="gpp-name">{name}</h1>

              {/* Badges */}
              <div className="gpp-badges">
                {guest.isVIP && (
                  <span className="gpp-badge vip">
                    <FontAwesomeIcon icon={faStar} /> VIP
                  </span>
                )}
                {activeBooking && (
                  <span className="gpp-badge inhouse">
                    <FontAwesomeIcon icon={faBed} /> In House
                  </span>
                )}
                <span className="gpp-badge pts">
                  <FontAwesomeIcon icon={faTrophy} /> {guest.loyaltyPoints ?? 0} pts
                </span>
              </div>

              {/* Stats */}
              <div className="gpp-stats">
                <div className="gpp-stat">
                  <span className="gpp-stat-val">EGP {totalSpent.toLocaleString()}</span>
                  <span className="gpp-stat-lbl">Total Spent</span>
                </div>
                <div className="gpp-stat">
                  <span className="gpp-stat-val">{bookings.length}</span>
                  <span className="gpp-stat-lbl">Bookings</span>
                </div>
                <div className="gpp-stat">
                  <span className="gpp-stat-val">{totalNights}</span>
                  <span className="gpp-stat-lbl">Nights</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="gpp-info-card">
              <h3 className="gpp-section-title">Contact</h3>
              <div className="gpp-info-list">
                <div className="gpp-info-row">
                  <FontAwesomeIcon icon={faPhone} className="gpp-info-icon" />
                  <span>{guest.phoneNumber || "—"}</span>
                </div>
                <div className="gpp-info-row">
                  <FontAwesomeIcon icon={faEnvelope} className="gpp-info-icon" />
                  <span>{guest.email || "—"}</span>
                </div>
                {guest.WaId && (
                  <div className="gpp-info-row">
                    <FontAwesomeIcon icon={faComment} className="gpp-info-icon" style={{ color: "#25D366" }} />
                    <a href={`https://wa.me/${guest.WaId}`} target="_blank" rel="noreferrer" style={{ color: "#25D366" }}>
                      {guest.WaId}
                    </a>
                  </div>
                )}
                <div className="gpp-info-row">
                  <FontAwesomeIcon icon={faCakeCandles} className="gpp-info-icon" />
                  <span>{fmt(guest.Birthday as string)}</span>
                </div>
                <div className="gpp-info-row">
                  <FontAwesomeIcon icon={faTag} className="gpp-info-icon" />
                  <span style={{ textTransform: "capitalize" }}>{guest.source || "manual"}</span>
                </div>
                {guest.RoomNumber && (
                  <div className="gpp-info-row">
                    <FontAwesomeIcon icon={faBed} className="gpp-info-icon" />
                    <span>Room {guest.RoomNumber}</span>
                  </div>
                )}
              </div>

              <button
                className="gpp-edit-btn"
                onClick={() => navigate(`/dashboard/manager/guests?edit=${guest.phoneNumber}`)}
              >
                <FontAwesomeIcon icon={faPen} /> Edit Profile
              </button>
              <button
                className="gpp-edit-btn"
                style={{ marginTop: 8, background: "rgba(201,168,76,0.1)", borderColor: "rgba(201,168,76,0.25)", color: "#C9A84C" }}
                onClick={sendFeedbackLink}
                disabled={fbGenerating || bookings.length === 0}
              >
                <FontAwesomeIcon icon={faLink} />
                {fbLinkCopied ? "Copied!" : fbGenerating ? "Generating…" : "Send Feedback Link"}
              </button>
            </div>

            {/* Passport / ID */}
            {passport && (
              <div className="gpp-info-card">
                <h3 className="gpp-section-title"><FontAwesomeIcon icon={faPassport} /> ID / Passport</h3>
                <div className="gpp-info-list" style={{ fontSize: 13 }}>
                  {passport.passport_number && (
                    <div className="gpp-info-row">
                      <span className="gpp-info-label">Passport #</span>
                      <span>{passport.passport_number}</span>
                    </div>
                  )}
                  {passport.nationality && (
                    <div className="gpp-info-row">
                      <span className="gpp-info-label">Nationality</span>
                      <span>{passport.nationality}</span>
                    </div>
                  )}
                  {passport.expiry_date && (
                    <div className="gpp-info-row">
                      <span className="gpp-info-label">Expires</span>
                      <span>{fmt(passport.expiry_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* ── CRM: Notes, Tags, Preferences ─────────────── */}
            <div className="gpp-info-card">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <h3 className="gpp-section-title" style={{ margin: 0 }}>
                  <FontAwesomeIcon icon={faBrain} style={{ marginRight: 7, color: "#C9A84C" }} />
                  CRM Notes
                </h3>
                {!crmEditing ? (
                  <button className="gpp-edit-btn" style={{ margin: 0, padding: "5px 12px", fontSize: 11 }}
                    onClick={() => { setCrmDraft({ notes: crm.notes, tags: crm.tags, preferences: crm.preferences }); setCrmEditing(true); }}>
                    <FontAwesomeIcon icon={faPen} /> Edit
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="gpp-edit-btn" style={{ margin: 0, padding: "5px 12px", fontSize: 11, background: "rgba(52,211,153,0.15)", borderColor: "rgba(52,211,153,0.3)", color: "#34d399" }}
                      onClick={saveCrm} disabled={crmSaving}>
                      <FontAwesomeIcon icon={faFloppyDisk} /> {crmSaving ? "Saving…" : "Save"}
                    </button>
                    <button className="gpp-edit-btn" style={{ margin: 0, padding: "5px 10px", fontSize: 11 }}
                      onClick={() => setCrmEditing(false)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 7 }}>Tags</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {(crmEditing ? crmDraft.tags : crm.tags || []).map((tag: string, i: number) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.15)", color: "#C9A84C", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                      {tag}
                      {crmEditing && (
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(242,235,217,0.45)", padding: 0, lineHeight: 1 }}
                          onClick={() => setCrmDraft(d => ({ ...d, tags: d.tags.filter((_: string, j: number) => j !== i) }))}>
                          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 9 }} />
                        </button>
                      )}
                    </span>
                  ))}
                  {crmEditing && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <input
                        style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 20, padding: "2px 10px", color: "#F2EBD9", fontSize: 11, outline: "none", width: 90 }}
                        placeholder="Add tag"
                        value={newTag}
                        onChange={e => setNewTag(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && newTag.trim()) {
                            setCrmDraft(d => ({ ...d, tags: [...d.tags, newTag.trim()] }));
                            setNewTag("");
                          }
                        }}
                      />
                      <button
                        style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 20, color: "#C9A84C", cursor: "pointer", padding: "2px 8px", fontSize: 10, fontWeight: 800 }}
                        onClick={() => { if (newTag.trim()) { setCrmDraft(d => ({ ...d, tags: [...d.tags, newTag.trim()] })); setNewTag(""); } }}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  )}
                  {!crmEditing && (crm.tags || []).length === 0 && (
                    <span style={{ color: "rgba(242,235,217,0.35)", fontSize: 12 }}>No tags</span>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 7 }}>Internal Notes</div>
                {crmEditing ? (
                  <textarea
                    style={{ width: "100%", boxSizing: "border-box", background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 9, padding: "9px 12px", color: "#F2EBD9", fontSize: 12.5, outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }}
                    rows={4}
                    placeholder="Notes about this guest — preferences, previous issues, special occasions…"
                    value={crmDraft.notes}
                    onChange={e => setCrmDraft(d => ({ ...d, notes: e.target.value }))}
                  />
                ) : crm.notes ? (
                  <p style={{ fontSize: 12.5, color: "rgba(242,235,217,0.3)", margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{crm.notes}</p>
                ) : (
                  <p style={{ fontSize: 12, color: "rgba(242,235,217,0.35)", margin: 0 }}>No notes yet</p>
                )}
              </div>

              {/* Preferences quick fields */}
              {crmEditing && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 9 }}>Preferences</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {[
                      { key: "preferred_room_type", label: "Preferred Room Type" },
                      { key: "dietary",             label: "Dietary Restrictions" },
                      { key: "pillow_preference",   label: "Pillow Preference" },
                      { key: "floor_preference",    label: "Floor Preference" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label style={{ fontSize: 10.5, color: "rgba(242,235,217,0.4)", display: "block", marginBottom: 3 }}>{label}</label>
                        <input
                          style={{ width: "100%", boxSizing: "border-box", background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 7, padding: "7px 11px", color: "#F2EBD9", fontSize: 12.5, outline: "none", fontFamily: "inherit" }}
                          placeholder={`e.g. ${key === "preferred_room_type" ? "Deluxe" : key === "dietary" ? "Vegetarian" : key === "pillow_preference" ? "Firm" : "High floor"}`}
                          value={crmDraft.preferences[key] || ""}
                          onChange={e => setCrmDraft(d => ({ ...d, preferences: { ...d.preferences, [key]: e.target.value } }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!crmEditing && Object.keys(crm.preferences || {}).some(k => crm.preferences[k]) && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 7 }}>Preferences</div>
                  {Object.entries(crm.preferences).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <span style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", minWidth: 130, textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</span>
                      <span style={{ fontSize: 12, color: "rgba(242,235,217,0.3)" }}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {crmToast && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#34d399", fontWeight: 700 }}>{crmToast}</div>
              )}
            </div>
          </aside>

          {/* ── RIGHT: Booking History ─────────────────────────── */}
          <main className="gpp-main">
            <div className="gpp-section-header">
              <h2 className="gpp-section-title"><FontAwesomeIcon icon={faCalendarCheck} /> Booking History</h2>
              <span className="gpp-count-badge">{bookings.length}</span>
            </div>

            {bookings.length === 0 ? (
              <div className="gpp-empty">No bookings found for this guest.</div>
            ) : (
              <div className="gpp-bookings">
                {bookings
                  .slice()
                  .sort((a: any, b: any) => {
                    const da = new Date(a.CheckIn || 0).getTime();
                    const db = new Date(b.CheckIn || 0).getTime();
                    return db - da; // newest first
                  })
                  .map((b: any) => {
                    const sc = STATUS_COLORS[(b.Status || "").toLowerCase()] ?? "#64748b";
                    const nights = Number(b.Nights) || 0;
                    const total  = Number(b.Total) || 0;
                    return (
                      <div key={b.BookingID} className="gpp-booking-card">
                        <div className="gpp-booking-header">
                          <div>
                            <span className="gpp-booking-id">{b.BookingID}</span>
                            <span className="gpp-booking-room">Room {b.RoomID || "—"}</span>
                          </div>
                          <span className="gpp-booking-status" style={{ color: sc, borderColor: `${sc}44`, background: `${sc}15` }}>
                            {b.Status}
                          </span>
                        </div>

                        <div className="gpp-booking-body">
                          <div className="gpp-booking-dates">
                            <div className="gpp-date-chip">
                              <span className="gpp-date-label">Check-in</span>
                              <span className="gpp-date-val">{fmt(b.CheckIn)}</span>
                            </div>
                            <div className="gpp-date-sep">→</div>
                            <div className="gpp-date-chip">
                              <span className="gpp-date-label">Check-out</span>
                              <span className="gpp-date-val">{fmt(b.CheckOut)}</span>
                            </div>
                            <div className="gpp-date-chip">
                              <span className="gpp-date-label">Nights</span>
                              <span className="gpp-date-val">{nights}</span>
                            </div>
                          </div>

                          <div className="gpp-booking-fin">
                            <div className="gpp-fin-row">
                              <FontAwesomeIcon icon={faMoneyBill} style={{ color: "#34d399", fontSize: 12 }} />
                              <span className="gpp-fin-label">Total</span>
                              <span className="gpp-fin-val">EGP {total.toLocaleString()}</span>
                            </div>
                            {nights > 0 && total > 0 && (
                              <div className="gpp-fin-row">
                                <span className="gpp-fin-label" style={{ marginLeft: 20 }}>Avg/night</span>
                                <span className="gpp-fin-val" style={{ color: "rgba(242,235,217,0.3)" }}>
                                  EGP {Math.round(total / nights).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>

                          {(b.notes || b.source) && (
                            <div className="gpp-booking-meta">
                              {b.source && (
                                <span className="gpp-meta-tag">
                                  <FontAwesomeIcon icon={faTag} style={{ marginRight: 4 }} />
                                  {b.source}
                                </span>
                              )}
                              {b.earlyCheckin && <span className="gpp-meta-tag">Early check-in</span>}
                              {b.lateCheckout && <span className="gpp-meta-tag">Late check-out</span>}
                              {b.notes && (
                                <div className="gpp-booking-notes">
                                  <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: 6, color: "#f59e0b" }} />
                                  {b.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            {/* Feedback */}
            {feedbackList.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div className="gpp-section-header">
                  <h2 className="gpp-section-title">
                    <FontAwesomeIcon icon={faChartBar} /> Guest Feedback
                  </h2>
                  <span className="gpp-count-badge">{feedbackList.length}</span>
                </div>
                <div className="gpp-bookings">
                  {feedbackList.map((fb: any) => {
                    const stars = (n: number) => "★".repeat(n) + "☆".repeat(5 - n);
                    return (
                      <div key={fb.id} className="gpp-booking-card">
                        <div className="gpp-booking-header">
                          <div>
                            <span className="gpp-booking-id">{fb.booking_id}</span>
                            <span style={{ fontSize: 11, color: "rgba(242,235,217,0.3)", marginLeft: 8 }}>
                              {fb.submitted_at ? new Date(fb.submitted_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                            </span>
                          </div>
                          <span style={{ color: "#fbbf24", fontSize: 16, letterSpacing: 1 }}>
                            {stars(fb.rating || 0)}
                          </span>
                        </div>
                        <div className="gpp-booking-body">
                          {/* Category scores */}
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
                            {[
                              { key: "cleanliness", label: "Cleanliness" },
                              { key: "service", label: "Service" },
                              { key: "room_comfort", label: "Room" },
                              { key: "food", label: "Food" },
                            ].filter(c => fb[c.key]).map(c => (
                              <div key={c.key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={{ fontSize: 9.5, fontWeight: 700, color: "rgba(242,235,217,0.3)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</span>
                                <span style={{ fontSize: 13, color: "#fbbf24" }}>{"★".repeat(fb[c.key])}{"☆".repeat(5 - fb[c.key])}</span>
                              </div>
                            ))}
                            {fb.would_return !== null && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={{ fontSize: 9.5, fontWeight: 700, color: "rgba(242,235,217,0.3)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Return?</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: fb.would_return ? "#34d399" : "#f87171" }}>
                                  {fb.would_return ? "Yes" : "No"}
                                </span>
                              </div>
                            )}
                          </div>
                          {fb.comment && (
                            <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "rgba(242,235,217,0.45)", fontStyle: "italic", borderLeft: "3px solid rgba(201,168,76,0.3)" }}>
                              "{fb.comment}"
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
}
